import type { ApiErrorBody } from "../types/api";
import { trackError, trackWarning } from "./observability";

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000/api/v1";
const BASE_URL = API_BASE_URL;
const TOKEN_KEY = "axiohire_token";
const REFRESH_TOKEN_KEY = "axiohire_refresh_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setAuthTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export class ApiError extends Error {
  code: string;
  status: number;
  details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  auth?: boolean;
  headers?: Record<string, string>;
  retryAfterRefresh?: boolean;
}

let refreshPromise: Promise<boolean> | null = null;

async function refreshAccessToken(): Promise<boolean> {
  const active = refreshPromise;
  if (active) return active;
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  refreshPromise = (async () => {
    try {
      const response = await fetch(buildUrl("/auth/refresh"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!response.ok) return false;
      const data = (await response.json()) as { access_token?: string; refresh_token?: string };
      if (!data.access_token || !data.refresh_token) return false;
      setAuthTokens(data.access_token, data.refresh_token);
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

/** Strip UUIDs / tokens from paths so logs stay aggregate-friendly and PII-free. */
function sanitizeApiPath(path: string): string {
  return path
    .replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      ":id",
    )
    .replace(/\/[A-Za-z0-9_-]{20,}/g, "/:token");
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const browserOrigin = typeof window === "undefined" ? "http://localhost" : window.location.origin;
  const url = new URL(`${BASE_URL}${path}`, browserOrigin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== "") url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

function logApiFailure(path: string, method: string, status: number, code: string): void {
  const ctx = {
    api_path: sanitizeApiPath(path),
    method,
    status,
    error_code: code,
  };
  if (status >= 500 || status === 0) {
    trackError("api.request_failed", ctx);
  } else if (status !== 401) {
    // Skip noisy 401s from expired sessions.
    trackWarning("api.request_rejected", ctx);
  }
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const {
    method = "GET",
    body,
    query,
    auth = true,
    headers: requestHeaders,
    retryAfterRefresh = true,
  } = options;
  const headers: Record<string, string> = { "Content-Type": "application/json", ...requestHeaders };
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    logApiFailure(path, method, 0, "network_error");
    throw new ApiError(0, "network_error", "Network request failed");
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : undefined;
  } catch {
    data = undefined;
  }

  if (!response.ok) {
    const errBody = data as ApiErrorBody | undefined;
    const code = errBody?.error?.code ?? "unknown_error";
    const message = errBody?.error?.message ?? `Request failed with status ${response.status}`;
    logApiFailure(path, method, response.status, code);
    if (response.status === 401 && auth && retryAfterRefresh && await refreshAccessToken()) {
      return apiRequest<T>(path, { ...options, retryAfterRefresh: false });
    }
    if (response.status === 401) {
      clearToken();
    }
    throw new ApiError(response.status, code, message, errBody?.error?.details);
  }

  return data as T;
}
